/**
 * Définitions des thèmes disponibles dans l'application
 * Structure extensible pour ajouter facilement de nouveaux thèmes
 */

export interface ThemeColors {
  primary: string;
  primaryLight: string;
  primaryDark: string;
  secondary: string;
  secondaryLight: string;
  secondaryDark: string;
  accent?: string;
  accentLight?: string;
  accentDark?: string;
  success: string;
  successLight: string;
  successDark: string;
  warning: string;
  warningLight: string;
  warningDark: string;
  error: string;
  errorLight: string;
  errorDark: string;
  background: string;
  backgroundSecondary: string;
  backgroundTertiary: string;
  foreground: string;
  foregroundSecondary: string;
  surface: string;
  surfaceElevated: string;
  surfaceOutlined: string;
  border: string;
  borderLight: string;
  borderMedium: string;
  borderDark: string;
  linkActive: string;
  neutral50: string;
  neutral100: string;
  neutral200: string;
  neutral300: string;
  neutral400: string;
  neutral500: string;
  neutral600: string;
  neutral700: string;
  neutral800: string;
  neutral900: string;
}

export interface Theme {
  name: string;
  displayName: string;
  colors: ThemeColors;
  previewColor: string; // Couleur pour preview dans le switcher
}

export const themes: Record<string, Theme> = {
  default: {
    name: 'default',
    displayName: 'Violet (Défaut)',
    previewColor: '#6D28D9',
    colors: {
      primary: '#6D28D9',
      primaryLight: '#8B5CF6',
      primaryDark: '#5B21B6',
      secondary: '#EC4899',
      secondaryLight: '#F472B6',
      secondaryDark: '#DB2777',
      accent: '#14B8A6',
      accentLight: '#2DD4BF',
      accentDark: '#0F766E',
      success: '#10B981',
      successLight: '#34D399',
      successDark: '#059669',
      warning: '#F59E0B',
      warningLight: '#FBBF24',
      warningDark: '#D97706',
      error: '#DC2626',
      errorLight: '#FEE2E2',
      errorDark: '#991B1B',
      background: '#FFFFFF',
      backgroundSecondary: '#F9FAFB',
      backgroundTertiary: '#F3F4F6',
      foreground: '#111827',
      foregroundSecondary: '#6B7280',
      surface: '#FFFFFF',
      surfaceElevated: '#FFFFFF',
      surfaceOutlined: '#FFFFFF',
      border: '#E5E7EB',
      borderLight: '#F3F4F6',
      borderMedium: '#D1D5DB',
      borderDark: '#9CA3AF',
      linkActive: '#6D28D9',
      neutral50: '#F9FAFB',
      neutral100: '#F3F4F6',
      neutral200: '#E5E7EB',
      neutral300: '#D1D5DB',
      neutral400: '#9CA3AF',
      neutral500: '#6B7280',
      neutral600: '#4B5563',
      neutral700: '#374151',
      neutral800: '#1F2937',
      neutral900: '#111827',
    },
  },
  alternative: {
    name: 'alternative',
    displayName: 'Bleu (Alternatif)',
    previewColor: '#3B82F6',
    colors: {
      primary: '#3B82F6',
      primaryLight: '#60A5FA',
      primaryDark: '#2563EB',
      secondary: '#8B5CF6',
      secondaryLight: '#A78BFA',
      secondaryDark: '#7C3AED',
      accent: '#14B8A6',
      accentLight: '#2DD4BF',
      accentDark: '#0F766E',
      success: '#10B981',
      successLight: '#34D399',
      successDark: '#059669',
      warning: '#F59E0B',
      warningLight: '#FBBF24',
      warningDark: '#D97706',
      error: '#DC2626',
      errorLight: '#FEE2E2',
      errorDark: '#991B1B',
      background: '#FFFFFF',
      backgroundSecondary: '#F9FAFB',
      backgroundTertiary: '#F3F4F6',
      foreground: '#111827',
      foregroundSecondary: '#6B7280',
      surface: '#FFFFFF',
      surfaceElevated: '#FFFFFF',
      surfaceOutlined: '#FFFFFF',
      border: '#E5E7EB',
      borderLight: '#F3F4F6',
      borderMedium: '#D1D5DB',
      borderDark: '#9CA3AF',
      linkActive: '#6D28D9',
      neutral50: '#F9FAFB',
      neutral100: '#F3F4F6',
      neutral200: '#E5E7EB',
      neutral300: '#D1D5DB',
      neutral400: '#9CA3AF',
      neutral500: '#6B7280',
      neutral600: '#4B5563',
      neutral700: '#374151',
      neutral800: '#1F2937',
      neutral900: '#111827',
    },
  },
  dark: {
    name: 'dark',
    displayName: 'Sombre',
    previewColor: '#0F172A',
    colors: {
      primary: '#A78BFA',
      primaryLight: '#C4B5FD',
      primaryDark: '#8B5CF6',
      secondary: '#F472B6',
      secondaryLight: '#F9A8D4',
      secondaryDark: '#EC4899',
      accent: '#2DD4BF',
      accentLight: '#5EEAD4',
      accentDark: '#14B8A6',
      success: '#34D399',
      successLight: '#6EE7B7',
      successDark: '#10B981',
      warning: '#FBBF24',
      warningLight: '#FCD34D',
      warningDark: '#F59E0B',
      error: '#F87171',
      errorLight: '#7F1D1D',
      errorDark: '#FCA5A5',
      background: '#0A0E1A',
      backgroundSecondary: '#151A28',
      backgroundTertiary: '#1E2538',
      foreground: '#F8FAFC',
      foregroundSecondary: '#CBD5E1',
      surface: '#151A28',
      surfaceElevated: '#1E2538',
      surfaceOutlined: '#252D42',
      border: '#334155',
      borderLight: '#475569',
      borderMedium: '#64748B',
      borderDark: '#94A3AF',
      linkActive: '#A78BFA',
      neutral50: '#1F2937',
      neutral100: '#374151',
      neutral200: '#4B5563',
      neutral300: '#6B7280',
      neutral400: '#9CA3AF',
      neutral500: '#D1D5DB',
      neutral600: '#E5E7EB',
      neutral700: '#F3F4F6',
      neutral800: '#F9FAFB',
      neutral900: '#FFFFFF',
    },
  },
  ocean: {
    name: 'ocean',
    displayName: 'Océan',
    previewColor: '#0891B2',
    colors: {
      primary: '#0891B2',
      primaryLight: '#06B6D4',
      primaryDark: '#0E7490',
      secondary: '#0EA5E9',
      secondaryLight: '#38BDF8',
      secondaryDark: '#0284C7',
      accent: '#14B8A6',
      accentLight: '#2DD4BF',
      accentDark: '#0F766E',
      success: '#10B981',
      successLight: '#34D399',
      successDark: '#059669',
      warning: '#F59E0B',
      warningLight: '#FBBF24',
      warningDark: '#D97706',
      error: '#DC2626',
      errorLight: '#FEE2E2',
      errorDark: '#991B1B',
      background: '#F8FAFC',
      backgroundSecondary: '#F1F5F9',
      backgroundTertiary: '#E2E8F0',
      foreground: '#0F172A',
      foregroundSecondary: '#475569',
      surface: '#FFFFFF',
      surfaceElevated: '#F8FAFC',
      surfaceOutlined: '#E2E8F0',
      border: '#CBD5E1',
      borderLight: '#E2E8F0',
      borderMedium: '#94A3B8',
      borderDark: '#64748B',
      linkActive: '#0891B2',
      neutral50: '#F8FAFC',
      neutral100: '#F1F5F9',
      neutral200: '#E2E8F0',
      neutral300: '#CBD5E1',
      neutral400: '#94A3B8',
      neutral500: '#64748B',
      neutral600: '#475569',
      neutral700: '#334155',
      neutral800: '#1E293B',
      neutral900: '#0F172A',
    },
  },
  forest: {
    name: 'forest',
    displayName: 'Forêt',
    previewColor: '#059669',
    colors: {
      primary: '#059669',
      primaryLight: '#10B981',
      primaryDark: '#047857',
      secondary: '#0891B2',
      secondaryLight: '#06B6D4',
      secondaryDark: '#0E7490',
      accent: '#14B8A6',
      accentLight: '#2DD4BF',
      accentDark: '#0F766E',
      success: '#22C55E',
      successLight: '#4ADE80',
      successDark: '#16A34A',
      warning: '#F59E0B',
      warningLight: '#FBBF24',
      warningDark: '#D97706',
      error: '#DC2626',
      errorLight: '#FEE2E2',
      errorDark: '#991B1B',
      background: '#F9FAFB',
      backgroundSecondary: '#F3F4F6',
      backgroundTertiary: '#E5E7EB',
      foreground: '#064E3B',
      foregroundSecondary: '#6B7280',
      surface: '#FFFFFF',
      surfaceElevated: '#F9FAFB',
      surfaceOutlined: '#E5E7EB',
      border: '#D1D5DB',
      borderLight: '#E5E7EB',
      borderMedium: '#9CA3AF',
      borderDark: '#6B7280',
      linkActive: '#059669',
      neutral50: '#F9FAFB',
      neutral100: '#F3F4F6',
      neutral200: '#E5E7EB',
      neutral300: '#D1D5DB',
      neutral400: '#9CA3AF',
      neutral500: '#6B7280',
      neutral600: '#4B5563',
      neutral700: '#374151',
      neutral800: '#1F2937',
      neutral900: '#111827',
    },
  },
  sunset: {
    name: 'sunset',
    displayName: 'Coucher de soleil',
    previewColor: '#F97316',
    colors: {
      primary: '#F97316',
      primaryLight: '#FB923C',
      primaryDark: '#EA580C',
      secondary: '#EC4899',
      secondaryLight: '#F472B6',
      secondaryDark: '#DB2777',
      accent: '#A855F7',
      accentLight: '#C084FC',
      accentDark: '#9333EA',
      success: '#10B981',
      successLight: '#34D399',
      successDark: '#059669',
      warning: '#FBBF24',
      warningLight: '#FCD34D',
      warningDark: '#F59E0B',
      error: '#DC2626',
      errorLight: '#FEE2E2',
      errorDark: '#991B1B',
      background: '#FFF7ED',
      backgroundSecondary: '#FFEDD5',
      backgroundTertiary: '#FED7AA',
      foreground: '#7C2D12',
      foregroundSecondary: '#9A3412',
      surface: '#FFFFFF',
      surfaceElevated: '#FFF7ED',
      surfaceOutlined: '#FFEDD5',
      border: '#FDBA74',
      borderLight: '#FED7AA',
      borderMedium: '#FB923C',
      borderDark: '#F97316',
      linkActive: '#F97316',
      neutral50: '#F9FAFB',
      neutral100: '#F3F4F6',
      neutral200: '#E5E7EB',
      neutral300: '#D1D5DB',
      neutral400: '#9CA3AF',
      neutral500: '#6B7280',
      neutral600: '#4B5563',
      neutral700: '#374151',
      neutral800: '#1F2937',
      neutral900: '#111827',
    },
  },
  gradient: {
    name: 'gradient',
    displayName: 'Dégradé Violet-Rose',
    previewColor: '#8B5CF6',
    colors: {
      primary: '#8B5CF6', // Violet vibrant
      primaryLight: '#A78BFA',
      primaryDark: '#7C3AED',
      secondary: '#EC4899', // Rose vibrant
      secondaryLight: '#F472B6',
      secondaryDark: '#DB2777',
      accent: '#D946EF',
      accentLight: '#F5D0FE',
      accentDark: '#A21CAF',
      success: '#10B981',
      successLight: '#34D399',
      successDark: '#059669',
      warning: '#F59E0B',
      warningLight: '#FBBF24',
      warningDark: '#D97706',
      error: '#DC2626',
      errorLight: '#FEE2E2',
      errorDark: '#991B1B',
      background: '#FFFFFF',
      backgroundSecondary: '#FDF4FF',
      backgroundTertiary: '#FAF5FF',
      foreground: '#1F2937',
      foregroundSecondary: '#6B7280',
      surface: '#FFFFFF',
      surfaceElevated: '#FFFFFF',
      surfaceOutlined: '#FFFFFF',
      border: '#DDD6FE',
      borderLight: '#F3E8FF',
      borderMedium: '#C4B5FD',
      borderDark: '#A78BFA',
      linkActive: '#8B5CF6',
      neutral50: '#FAF5FF',
      neutral100: '#F3E8FF',
      neutral200: '#E9D5FF',
      neutral300: '#DDD6FE',
      neutral400: '#C4B5FD',
      neutral500: '#A78BFA',
      neutral600: '#8B5CF6',
      neutral700: '#7C3AED',
      neutral800: '#6D28D9',
      neutral900: '#5B21B6',
    },
  },
  pastel: {
    name: 'pastel',
    displayName: 'Pastel Violet-Rose',
    previewColor: '#A78BFA',
    colors: {
      primary: '#A78BFA', // Violet pastel plus soutenu
      primaryLight: '#C4B5FD',
      primaryDark: '#8B5CF6',
      secondary: '#F472B6', // Rose pastel plus soutenu
      secondaryLight: '#F9A8D4',
      secondaryDark: '#EC4899',
      accent: '#F472B6',
      accentLight: '#F9A8D4',
      accentDark: '#EC4899',
      success: '#10B981',
      successLight: '#34D399',
      successDark: '#059669',
      warning: '#F59E0B',
      warningLight: '#FBBF24',
      warningDark: '#D97706',
      error: '#DC2626',
      errorLight: '#FEE2E2',
      errorDark: '#991B1B',
      background: '#FFFFFF',
      backgroundSecondary: '#FDF4FF',
      backgroundTertiary: '#FAF5FF',
      foreground: '#1F2937',
      foregroundSecondary: '#6B7280',
      surface: '#FFFFFF',
      surfaceElevated: '#FFFFFF',
      surfaceOutlined: '#FFFFFF',
      border: '#E9D5FF',
      borderLight: '#FAF5FF',
      borderMedium: '#DDD6FE',
      borderDark: '#C4B5FD',
      linkActive: '#A78BFA',
      neutral50: '#FAF5FF',
      neutral100: '#F3E8FF',
      neutral200: '#E9D5FF',
      neutral300: '#DDD6FE',
      neutral400: '#C4B5FD',
      neutral500: '#A78BFA',
      neutral600: '#8B5CF6',
      neutral700: '#7C3AED',
      neutral800: '#6D28D9',
      neutral900: '#5B21B6',
    },
  },
};

export const DEFAULT_THEME = 'default';
export const THEME_STORAGE_KEY = 'cashlive-theme';

/**
 * Obtient le thème depuis localStorage pour une organisation spécifique
 * Si organizationId est fourni, utilise une clé spécifique à l'organisation
 * Sinon, utilise la clé globale (pour les pages non-organisation)
 */
export const getStoredTheme = (organizationId?: string): string => {
  if (typeof window === 'undefined') return DEFAULT_THEME;

  const storageKey = organizationId
    ? `${THEME_STORAGE_KEY}-${organizationId}`
    : THEME_STORAGE_KEY;

  const stored = localStorage.getItem(storageKey);
  return stored && themes[stored] ? stored : DEFAULT_THEME;
};

/**
 * Sauvegarde le thème dans localStorage pour une organisation spécifique
 * Si organizationId est fourni, utilise une clé spécifique à l'organisation
 * Sinon, utilise la clé globale (pour les pages non-organisation)
 */
export const storeTheme = (themeName: string, organizationId?: string): void => {
  if (typeof window === 'undefined') return;
  if (themes[themeName]) {
    const storageKey = organizationId
      ? `${THEME_STORAGE_KEY}-${organizationId}`
      : THEME_STORAGE_KEY;
    localStorage.setItem(storageKey, themeName);
  }
};

